import type { FC } from 'react';
import type { CalendarProviderProps } from '@/components/calendar/context';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { CalendarContext } from '@/components/calendar/context';
import {
  adjustDateToPeriod,
  generateCalendar,
  getNextIndex,
  getYearsBetween,
  isDateInPeriod,
} from '@/components/calendar/utils';
import Converter from '@/utils/converter';

const CalendarProvider: FC<CalendarProviderProps> = ({
  inputBorderRef,
  name,
  value,
  precision,
  period,
  startPosition,
  dayNames,
  monthNames,
  weekStart,
  modalTill,
  closeButton,
  modalPosition,
  onDateChange,
  onOpenModal,
  onCloseModal,
  children,
}) => {
  const yearList = getYearsBetween(period);
  const dateFromMaskValue = Converter.DateSting.ToDate(value);
  const date = isDateInPeriod(dateFromMaskValue, period) ? dateFromMaskValue : null;

  const [monthIndex, setMonthIndex] = useState<number>(-1);
  const [yearIndex, setYearIndex] = useState<number>(-1);
  const [open, setOpen] = useState<boolean>(false);

  const onMonthIndexChange = useCallback(
    (newMonthIndex: number) => {
      setMonthIndex(newMonthIndex);
      if (newMonthIndex === 11 && monthIndex === 0) {
        setYearIndex((prev) => getNextIndex(yearList, prev, 'backward'));
      }
      if (newMonthIndex === 0 && monthIndex === 11) {
        setYearIndex((prev) => getNextIndex(yearList, prev, 'forward'));
      }
    },
    [monthIndex, yearList],
  );

  const onYearIndexChange = useCallback((newYearIndex: number) => setYearIndex(newYearIndex), []);

  const onCalendarClick = useCallback(
    (date: Date): void => {
      setOpen(false);
      onDateChange(date);
    },
    [onDateChange, period],
  );

  const calendarData = useMemo(
    () => generateCalendar(weekStart, [monthIndex, yearList[yearIndex]], period),
    [monthIndex, yearIndex, yearList, period, weekStart],
  );

  useEffect(() => {
    const newDate = Converter.DateSting.ToDate(value);
    if (!open && newDate && value && isDateInPeriod(newDate, period)) {
      setMonthIndex(newDate.getMonth());
      setYearIndex(yearList.indexOf(newDate.getFullYear()));
    }
    if (!open && newDate && value && !isDateInPeriod(newDate, period)) {
      const initialDate = adjustDateToPeriod(newDate, startPosition, period);
      setMonthIndex(initialDate.getMonth());
      setYearIndex(yearList.indexOf(initialDate.getFullYear()));
    }
    if (!open && !newDate) {
      const initialDate = adjustDateToPeriod(date, startPosition, period);
      setMonthIndex(initialDate.getMonth());
      setYearIndex(yearList.indexOf(initialDate.getFullYear()));
    }
  }, [value, period, yearList, open, startPosition, date]);

  return (
    <CalendarContext.Provider
      value={{
        inputBorderRef,
        name,
        period,
        precision,
        startPosition,
        weekStart,
        closeButton,
        dayNames,
        monthNames,
        modalTill,
        onDateChange,
        onOpenModal,
        onCloseModal,
        calendarData,
        monthIndex,
        yearIndex,
        open,
        date,
        yearList,
        modalPosition,
        onMonthIndexChange,
        onYearIndexChange,
        onCalendarClick,
        onCloseButton: () => setOpen(false),
        onCalendarButton: () => setOpen(true),
        onOutsideClick: () => setOpen(false),
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export default CalendarProvider;
